import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
    EnumGlobalRoutes,
    EnumTenantAdminRoutes,
} from '@/app/router/routerConstants'
import UsersSvg from '@/assets/img/card-decor-1.svg'
import TeamsSvg from '@/assets/img/card-decor-2.svg'
import WorkGroupsSvg from '@/assets/img/card-decor-3.svg'
import ActiveOrdersSvg from '@/assets/img/card-decor-4.svg'
import CompletedOrdersSvg from '@/assets/img/card-decor-5.svg'
import Delete from '@/assets/img/delete-icon.svg?react'
import DotsSvg from '@/assets/img/dots.svg'
import ProfileIcon from '@/assets/img/ico-view-profile.svg?react'
import Dropdown from '@/compoments/common/Dropdown'
import UserPart from '@/compoments/common/UserPart'
import UsersGroup from '@/compoments/common/UsersGroup'
import DropdownList from '@/compoments/DropdownList'
import PageLoader from '@/compoments/PageLoader'
import { useAppSelector } from '@/hooks/redux'
import { useDeleteTeamMutation } from '@/services/tenant/teamManagement'
import { useDeleteUserMutation } from '@/services/tenant/users'
import { useGetDashboardTenantQuery } from '@/services/tenant/webPage'
import type { UserRoleEnum } from '@/types'
import { ROLE_LABELS } from '@/types'
import type { ITeamMember } from '@/types/tenant/team.types'

export const AdminDashboard = () => {
    const { data, isLoading } = useGetDashboardTenantQuery('')
    const [deleteTeam] = useDeleteTeamMutation()
    const [deleteUser] = useDeleteUserMutation()
    const navigate = useNavigate()
    const onlineUsers = useAppSelector(state => state.app.onlineUsers)

    const pageData = data?.data

    const randomUsers = useMemo(() => {
        return pageData?.randomUsers?.map(user => {
            return {
                ...user,
                status:
                    onlineUsers.find(u => u.id === user.id)?.status ||
                    user.status,
            }
        })
    }, [data, onlineUsers])

    if (isLoading) return <PageLoader />
    // if (!data) return null

    const deleteHandler = async (id: number) => {
        const { toast } = await import('react-toastify')
        try {
            const res = await deleteTeam(id).unwrap()
            if (res?.success) {
                toast.success('Deleted successfully!')
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('rejected', error)
        }
    }
    const deleteUserHandler = async (id: number) => {
        const { toast } = await import('react-toastify')
        try {
            const res = await deleteUser(id).unwrap()
            if (res?.success) {
                toast.success('Deleted successfully!')
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('rejected', error)
        }
    }

    return (
        <main className="content flex flex-col">
            <div className="px-container flex flex-col flex-auto">
                {!data?.data && !isLoading && !data?.success ? (
                    <h4 className="text-center mt-4">{data?.message}</h4>
                ) : (
                    <>
                        <div className="info-card-list mb-13">
                            <div className="info-card-item">
                                <div className="info-card">
                                    <div className="info-card__decor-group">
                                        <div className="info-card__img-decor">
                                            <img
                                                src={UsersSvg}
                                                alt=""
                                            />
                                        </div>
                                        <span>Users</span>
                                    </div>
                                    <span className="info-cunter">
                                        {pageData?.usersCount}
                                    </span>
                                </div>
                            </div>
                            <div className="info-card-item">
                                <div className="info-card">
                                    <div className="info-card__decor-group">
                                        <div className="info-card__img-decor">
                                            <img
                                                src={TeamsSvg}
                                                alt=""
                                            />
                                        </div>
                                        <span>Teams</span>
                                    </div>
                                    <span className="info-cunter">
                                        {pageData?.teamsCount}
                                    </span>
                                </div>
                            </div>
                            <div className="info-card-item">
                                <div className="info-card">
                                    <div className="info-card__decor-group">
                                        <div className="info-card__img-decor">
                                            <img
                                                src={WorkGroupsSvg}
                                                alt=""
                                            />
                                        </div>
                                        <span>WorkGroups</span>
                                    </div>
                                    <span className="info-cunter">
                                        {pageData?.wgCount}
                                    </span>
                                </div>
                            </div>
                            <div className="info-card-item">
                                <div className="info-card">
                                    <div className="info-card__decor-group">
                                        <div className="info-card__img-decor">
                                            <img
                                                src={ActiveOrdersSvg}
                                                alt=""
                                            />
                                        </div>
                                        <span>Active Orders</span>
                                    </div>
                                    <span className="info-cunter">
                                        {pageData?.activeOrdersCount}
                                    </span>
                                </div>
                            </div>
                            <div className="info-card-item">
                                <div className="info-card">
                                    <div className="info-card__decor-group">
                                        <div className="info-card__img-decor">
                                            <img
                                                src={CompletedOrdersSvg}
                                                alt=""
                                            />
                                        </div>
                                        <span>Completed Orders</span>
                                    </div>
                                    <span className="info-cunter">
                                        {pageData?.completedOrdersCount}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="row gx-16 gy-28 flex-auto">
                            <div className="col-lg-6">
                                <div className="table-group">
                                    <div className="table-header flex items-center justify-between flex-wrap p-20">
                                        <h5>User Management</h5>

                                        <div className="flex items-center gap-32">
                                            <Link
                                                to={
                                                    EnumTenantAdminRoutes.TENANT_ADMIN_USER_MANAGEMENT
                                                }
                                                className="btn color-primary gap-6 text--xs fw-500"
                                            >
                                                <span className="text--xs">
                                                    Show All
                                                </span>
                                                <svg
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 14 14"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M5.25 2.625L9.625 7L5.25 11.375"
                                                        stroke="#232ED1"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    ></path>
                                                </svg>
                                            </Link>
                                        </div>
                                    </div>

                                    <div className="table-body table-responsive p-0">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Full Name</th>
                                                    <th>Roles</th>
                                                    {/* <th
                                                        style={{
                                                            width: '10px',
                                                        }}
                                                    >
                                                        Status
                                                    </th> */}
                                                    <th
                                                        style={{
                                                            width: '10px',
                                                        }}
                                                    >
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {randomUsers?.map(user => (
                                                    <tr key={user.id}>
                                                        <td>
                                                            <UserPart
                                                                src={user.photo}
                                                                name={
                                                                    user.fullName
                                                                }
                                                                email={
                                                                    user.email
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            {user.roles.map(
                                                                (
                                                                    role: UserRoleEnum,
                                                                    index,
                                                                ) => (
                                                                    <span
                                                                        key={
                                                                            role
                                                                        }
                                                                        className="role-label"
                                                                    >
                                                                        {
                                                                            ROLE_LABELS[
                                                                                role
                                                                            ]
                                                                        }
                                                                        {index <
                                                                        user
                                                                            .roles
                                                                            .length -
                                                                            1
                                                                            ? ', '
                                                                            : ''}
                                                                    </span>
                                                                ),
                                                            )}
                                                        </td>
                                                        {/* <td>
                                                                <PillStatus
                                                                    status={
                                                                        user.status as string
                                                                    }
                                                                />
                                                            </td> */}
                                                        <td className="text-right sticky-end">
                                                            <Dropdown
                                                                closeSelf={true}
                                                                dropdownClasses="flex flex-col"
                                                                button={
                                                                    <span className="ico">
                                                                        <img
                                                                            src={
                                                                                DotsSvg
                                                                            }
                                                                            alt=""
                                                                        />
                                                                    </span>
                                                                }
                                                                menuClassName="dropdown-md"
                                                                menu={
                                                                    <DropdownList
                                                                        list={[
                                                                            {
                                                                                title: 'View Profile',
                                                                                icon: (
                                                                                    <ProfileIcon />
                                                                                ),
                                                                                action: () =>
                                                                                    navigate(
                                                                                        EnumGlobalRoutes.USER_PAGE +
                                                                                            user.id,
                                                                                    ),
                                                                            },
                                                                            {
                                                                                title: 'Delete',
                                                                                icon: (
                                                                                    <Delete />
                                                                                ),
                                                                                action: () => {
                                                                                    deleteUserHandler(
                                                                                        user.id,
                                                                                    )
                                                                                },
                                                                                type: 'btn-drp-link-danger',
                                                                            },
                                                                        ]}
                                                                    />
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="table-group">
                                    <div className="table-header flex items-center justify-between flex-wrap p-20">
                                        <h5>Teams</h5>

                                        <Link
                                            to={
                                                EnumTenantAdminRoutes.TENANT_ADMIN_TEAM_MANAGEMENT
                                            }
                                            className="btn color-primary gap-6 text--xs fw-500"
                                        >
                                            <span className="text--xs">
                                                Show All
                                            </span>
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 14 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M5.25 2.625L9.625 7L5.25 11.375"
                                                    stroke="#232ED1"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                ></path>
                                            </svg>
                                        </Link>
                                    </div>

                                    <div className="table-body table-responsive p-0">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Team</th>
                                                    <th>Team Lead</th>
                                                    <th>Users</th>
                                                    <th
                                                        style={{
                                                            width: '10px',
                                                        }}
                                                    >
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            {pageData?.randomTeams?.map(
                                                (team, index) => (
                                                    <tbody key={index}>
                                                        <tr>
                                                            <td>{team.name}</td>

                                                            <td>
                                                                {team?.lead && (
                                                                    <UserPart
                                                                        src={
                                                                            team
                                                                                .lead
                                                                                .photo
                                                                        }
                                                                        name={
                                                                            team
                                                                                .lead
                                                                                .fullName
                                                                        }
                                                                        email={
                                                                            team
                                                                                .lead
                                                                                .email
                                                                        }
                                                                    />
                                                                )}
                                                            </td>

                                                            <td>
                                                                <UsersGroup
                                                                    list={team.randomUsers.map(
                                                                        (
                                                                            el: ITeamMember,
                                                                        ) => ({
                                                                            src: el.photo,
                                                                        }),
                                                                    )}
                                                                    usersCount={
                                                                        team.totalUsers
                                                                    }
                                                                />
                                                            </td>

                                                            <td className="text-right sticky-end">
                                                                <Dropdown
                                                                    closeSelf={
                                                                        true
                                                                    }
                                                                    dropdownClasses="flex flex-col"
                                                                    button={
                                                                        <span className="ico">
                                                                            <img
                                                                                src={
                                                                                    DotsSvg
                                                                                }
                                                                                alt=""
                                                                            />
                                                                        </span>
                                                                    }
                                                                    menuClassName="dropdown-md"
                                                                    menu={
                                                                        <DropdownList
                                                                            list={[
                                                                                {
                                                                                    title: 'Delete',
                                                                                    icon: (
                                                                                        <Delete />
                                                                                    ),
                                                                                    action: async () =>
                                                                                        deleteHandler(
                                                                                            team
                                                                                                .team
                                                                                                .id,
                                                                                        ),
                                                                                    type: 'btn-drp-link-danger',
                                                                                },
                                                                            ]}
                                                                        />
                                                                    }
                                                                />
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                ),
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </main>
    )
}
